import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { parseLocaleNum } from "../lib/locale";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, Info, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { vendor_id: "", tanggal: today(), jumlah_kasbon: 0, alasan: "" };

export default function KasbonPage() {
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [fVendor, setFVendor] = useState("all");
    const [fStatus, setFStatus] = useState("all");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [status, setStatus] = useState("berjalan");
    const [submitting, setSubmitting] = useState(false);
    const [estimasi, setEstimasi] = useState(null);
    const [loadingEst, setLoadingEst] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (fVendor !== "all") params.vendor_id = fVendor;
            if (fStatus !== "all") params.status = fStatus;
            const { data } = await api.get("/kasbon", { params });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { (async () => { const v = await api.get("/vendors"); setVendors(v.data); })(); }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, fVendor, fStatus]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const loadEstimasi = async (vendor_id, excludeId = "") => {
        if (!vendor_id) { setEstimasi(null); return; }
        setLoadingEst(true);
        try {
            const params = {};
            if (excludeId) params.exclude_kasbon_id = excludeId;
            const { data } = await api.get(`/vendors/${vendor_id}/estimasi-pendapatan`, { params });
            setEstimasi(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoadingEst(false); }
    };

    const openCreate = () => {
        setEditing(null); setForm(emptyForm); setStatus("berjalan"); setEstimasi(null); setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({ vendor_id: row.vendor_id, tanggal: row.tanggal, jumlah_kasbon: row.jumlah_kasbon, alasan: row.alasan || "" });
        setStatus(row.status); setDialogOpen(true);
        loadEstimasi(row.vendor_id, row.id);
    };

    useEffect(() => {
        if (dialogOpen && form.vendor_id && !editing) loadEstimasi(form.vendor_id);
        // eslint-disable-next-line
    }, [form.vendor_id, dialogOpen]);

    const overPlafon = estimasi && parseLocaleNum(form.jumlah_kasbon || 0) > (estimasi.sisa_plafon || 0);

    const save = async () => {
        if (!form.vendor_id) return toast.error("Vendor wajib dipilih");
        if (parseLocaleNum(form.jumlah_kasbon) <= 0) return toast.error("Jumlah kasbon harus > 0");
        setSubmitting(true);
        try {
            const payload = { ...form, jumlah_kasbon: parseFloat(form.jumlah_kasbon) };
            if (editing) {
                await api.put(`/kasbon/${editing.id}`, { ...payload, status });
                toast.success("Kasbon diperbarui");
            } else {
                await api.post("/kasbon", payload);
                toast.success("Kasbon ditambahkan");
            }
            setDialogOpen(false); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try { await api.delete(`/kasbon/${confirmDelete.id}`); toast.success("Dipindahkan ke Riwayat Terhapus. Buka Riwayat untuk pulihkan."); setConfirmDelete(null); load(); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="kasbon-page">
            <PageHeader
                title="Kasbon Vendor"
                description="Pinjaman / uang muka vendor. Dibatasi otomatis oleh plafon estimasi pendapatan."
                actions={canWrite() && (
                    <Button onClick={openCreate} data-testid="kasbon-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Tambah Kasbon
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input placeholder="Cari nomor kasbon..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="kasbon-search-input" className="pl-9 rounded-lg h-10" />
                    </div>
                    <Select value={fVendor} onValueChange={setFVendor}>
                        <SelectTrigger className="rounded-lg h-10"><SelectValue placeholder="Semua vendor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={fStatus} onValueChange={setFStatus}>
                        <SelectTrigger className="rounded-lg h-10"><SelectValue placeholder="Semua status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua status</SelectItem>
                            <SelectItem value="berjalan">Berjalan</SelectItem>
                            <SelectItem value="lunas">Lunas</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={Wallet} title="Belum ada kasbon" description="Belum ada kasbon vendor." testid="kasbon-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => (
                    <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`kasbon-card-${row.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_kasbon}</div>
                                <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || row.nama_vendor_snapshot || "-"}</div>
                            </div>
                            <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${row.status === "berjalan" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{row.status}</span>
                        </div>
                        <div className="text-sm font-semibold mt-1 num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.jumlah_kasbon)}</div>
                        {row.alasan && <div className="text-xs mt-1 truncate" style={{ color: "var(--text-muted)" }}>{row.alasan}</div>}
                        <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal)}</div>
                            <div className="flex items-center gap-1">
                                {canWrite() && <button onClick={() => openEdit(row)} data-testid={`kasbon-edit-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`kasbon-delete-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="kasbon-data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nomor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Jumlah</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Alasan</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Status</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-28" style={{ color: "var(--text-muted)" }}>Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><EmptyState icon={Wallet} title="Belum ada kasbon" description="Belum ada kasbon vendor." testid="kasbon-empty-desktop" /></TableCell></TableRow>
                        ) : pageItems.map((row) => (
                            <TableRow key={row.id} data-testid={`kasbon-row-${row.id}`}>
                                <TableCell className="font-semibold" style={{ color: "var(--apple-blue)" }}>{row.nomor_kasbon}</TableCell>
                                <TableCell className="text-sm" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || row.nama_vendor_snapshot || "-"}</TableCell>
                                <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal)}</TableCell>
                                <TableCell className="text-sm font-semibold text-right num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.jumlah_kasbon)}</TableCell>
                                <TableCell className="text-sm max-w-xs truncate" style={{ color: "var(--text-muted)" }}>{row.alasan || "-"}</TableCell>
                                <TableCell><span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${row.status === "berjalan" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{row.status}</span></TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {canWrite() && <button onClick={() => openEdit(row)} data-testid={`kasbon-edit-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                        {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`kasbon-delete-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length} data</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs px-2" style={{ color: "var(--text-secondary)" }}>Hal {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="kasbon-modal-dialog" className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Kasbon" : "Tambah Kasbon"}</DialogTitle>
                        <DialogDescription>Sistem akan menolak kasbon yang melebihi plafon estimasi pendapatan vendor.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Vendor <span className="text-rose-600">*</span></Label>
                                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })} disabled={!!editing}>
                                    <SelectTrigger data-testid="kasbon-form-vendor" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih vendor" /></SelectTrigger>
                                    <SelectContent>
                                        {vendors.filter((v) => v.status === "aktif").map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Tanggal <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        {form.vendor_id && (
                            <div className="p-4 rounded-xl bg-teal-50/40 border border-teal-200/70">
                                <div className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-2 flex items-center gap-1.5">
                                    <Info className="w-3.5 h-3.5" /> Plafon Vendor
                                    {loadingEst && <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto" />}
                                </div>
                                {estimasi && (
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm num-tabular">
                                        <div className="text-slate-600">Total setor:</div><div className="text-right font-medium">{formatRupiah(estimasi.total_setor)}</div>
                                        <div className="text-slate-600">WIP dari SPK aktif:</div><div className="text-right font-medium">{formatRupiah(estimasi.wip_dari_spk)}</div>
                                        <div className="text-slate-600">− Bon kain:</div><div className="text-right font-medium">{formatRupiah(estimasi.total_bon_kain)}</div>
                                        <div className="text-slate-600">− Bon peralatan:</div><div className="text-right font-medium">{formatRupiah(estimasi.total_bon_peralatan)}</div>
                                        <div className="text-slate-600">− Kasbon berjalan{editing ? " (kecuali ini)" : ""}:</div><div className="text-right font-medium">{formatRupiah(estimasi.total_kasbon_berjalan)}</div>
                                        <div className="col-span-2 border-t border-teal-200/70 pt-1.5 mt-1">
                                            <div className="flex justify-between">
                                                <span className="text-sm font-bold text-slate-800">Sisa plafon:</span>
                                                <span data-testid="kasbon-sisa-plafon" className={`text-base font-extrabold ${estimasi.sisa_plafon > 0 ? "text-teal-700" : "text-rose-700"}`}>{formatRupiah(estimasi.sisa_plafon)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Jumlah Kasbon (Rp) <span className="text-rose-600">*</span></Label>
                                <Input type="text" inputMode="decimal" value={form.jumlah_kasbon} onChange={(e) => setForm({ ...form, jumlah_kasbon: e.target.value })} data-testid="kasbon-form-jumlah" className={`mt-1.5 rounded-xl ${overPlafon ? "border-rose-400 focus-visible:ring-rose-400" : ""}`} />
                                {overPlafon && <div className="text-xs text-rose-600 mt-1 flex items-center gap-1"><Info className="w-3 h-3" /> Melebihi sisa plafon</div>}
                            </div>
                            {editing && (
                                <div>
                                    <Label>Status</Label>
                                    <Select value={status} onValueChange={setStatus}>
                                        <SelectTrigger data-testid="kasbon-form-status" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="berjalan">Berjalan</SelectItem>
                                            <SelectItem value="lunas">Lunas</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>

                        <div>
                            <Label>Alasan / Catatan</Label>
                            <Textarea value={form.alasan} onChange={(e) => setForm({ ...form, alasan: e.target.value })} className="mt-1.5 rounded-xl" rows={2} placeholder="Contoh: uang muka bahan, biaya darurat operasional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Batal</Button>
                        <Button onClick={save} disabled={submitting || (overPlafon && status === "berjalan")} data-testid="kasbon-modal-save-button" className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="md:rounded-2xl">
                    <AlertDialogHeader><AlertDialogTitle>Hapus Kasbon (ke Riwayat)</AlertDialogTitle><AlertDialogDescription><span className="font-semibold">{confirmDelete?.nomor_kasbon}</span> akan dipindahkan ke <b>Riwayat Terhapus</b> selama 30 hari dan bisa dipulihkan kapan saja. Lanjut?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>{deleting ? "..." : "Hapus (ke Riwayat)"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
